import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { getTraineeCertificates, GenerateCertificate } from '../../../services/userService';
import { toast } from 'react-toastify';
import NoData from '../../../components/common/NoData';

function UserCertificates() {
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedCertificate, setSelectedCertificate] = useState(null);
  const [traineeCertificates, setTraineeCertificates] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const fetchCertificates = async (showRefreshIndicator = false) => {
    try {
      if (showRefreshIndicator) setIsRefreshing(true);
      const response = await getTraineeCertificates();
      if (response.success) {
        setTraineeCertificates(response.data.certificates || []);
      }
    } catch (error) {
      console.error('Error fetching certificates:', error);
      toast.error('Error fetching certificates');
    } finally {
      if (showRefreshIndicator) setIsRefreshing(false);
    }
  };

  const handleGenerateCertificate = async () => {
    setIsGenerating(true);
    try {
      const response = await GenerateCertificate();
      if (response.success) {
        await fetchCertificates(true);
      } else {
        toast.error('Failed to generate certificate');
      }
    } catch (error) {
      console.error('Error generating certificate:', error);
      toast.info('No completed courses available for certificate');
      await fetchCertificates(true);
    } finally {
      setIsGenerating(false);
    }
  };

  useEffect(() => {
    fetchCertificates();
  }, []);

  const handleDownload = (certificateUrl) => {
    try {
      window.open(certificateUrl, '_blank');
    } catch (error) {
      toast.error('Error opening certificate');
    }
  };

  const openCertificateModal = (cert) => {
    setSelectedCertificate(cert);
    setShowDetailsModal(true);
  };

  const openCertificatePreview = (cert, e) => {
    e?.stopPropagation();
    setSelectedCertificate(cert);
    setShowPreviewModal(true);
  };

  const gradientBg = (index) => {
    const classes = [
      'from-[var(--primary-color)]/5 to-[var(--primary-light)]/10',
      'from-emerald-500/5 to-teal-500/10',
      'from-amber-500/5 to-orange-500/10',
      'from-violet-500/5 to-purple-500/10',
    ];
    return classes[index % 4];
  };

  return (
    <div className="certificates-page min-h-[60vh]">
      {/* Generate bar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 p-5 rounded-2xl bg-[var(--bg-primary)] border border-[var(--border-color)] shadow-sm">
        <p className="text-sm text-[var(--text-secondary)]">
          After 100% course completion, click below to generate your certificate.
        </p>
        <button
          type="button"
          onClick={handleGenerateCertificate}
          disabled={isGenerating}
          className="flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-semibold text-white bg-[var(--primary-color)] hover:bg-[var(--primary-hover)] disabled:opacity-70 disabled:cursor-not-allowed transition-colors shadow-md hover:shadow-lg shrink-0"
        >
          {isGenerating ? (
            <>
              <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <Icon icon="mdi:certificate" width={22} height={22} />
              Generate Certificate
            </>
          )}
        </button>
      </div>

      {isRefreshing && (
        <div className="flex items-center justify-center gap-2 py-4 text-[var(--text-secondary)] text-sm">
          <span className="w-5 h-5 border-2 border-[var(--primary-color)] border-t-transparent rounded-full animate-spin" />
          Refreshing certificates...
        </div>
      )}

      {/* Certificate grid */}
      {traineeCertificates.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 sm:gap-6">
          {traineeCertificates.map((cert, index) => (
            <div
              key={cert.id}
              className={`certificate-card group bg-[var(--bg-primary)] rounded-2xl border border-[var(--border-color)] overflow-hidden shadow-sm hover:shadow-xl hover:border-[var(--primary-lighter)] transition-all duration-300 flex flex-col h-full bg-gradient-to-br ${gradientBg(index)}`}
            >
              <div className="relative flex-1 min-h-[220px] p-4 flex items-center justify-center">
                <img
                  src={cert.certificate_url}
                  alt={cert.certificate_name}
                  className="w-full h-auto max-h-[220px] object-contain rounded-xl bg-white/80 p-3 shadow-sm"
                />
                <div className="absolute inset-4 flex items-center justify-center rounded-xl bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    type="button"
                    onClick={(e) => openCertificatePreview(cert, e)}
                    className="p-3 rounded-full bg-white text-[var(--text-primary)] hover:bg-[var(--primary-color)] hover:text-white transition-colors shadow-lg"
                    aria-label="Preview certificate"
                  >
                    <Icon icon="mdi:eye-outline" width={24} height={24} />
                  </button>
                </div>
              </div>
              <div className="p-5 border-t border-[var(--border-color)] bg-[var(--bg-primary)]/80">
                <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2 line-clamp-2 min-h-[2.5rem]">
                  {cert.certificate_name}
                </h3>
                <p className="flex items-center gap-2 text-sm text-[var(--text-secondary)] mb-4">
                  <Icon icon="mdi:calendar-outline" width={16} height={16} />
                  {new Date(cert.issued_date).toLocaleDateString(undefined, { dateStyle: 'medium' })}
                </p>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => handleDownload(cert.certificate_url)}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold text-white bg-[var(--primary-color)] hover:bg-[var(--primary-hover)] transition-colors shadow-sm"
                  >
                    <Icon icon="mdi:download" width={18} height={18} />
                    Download
                  </button>
                  <button
                    type="button"
                    onClick={() => openCertificateModal(cert)}
                    className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-[var(--text-secondary)] border border-[var(--border-color)] bg-[var(--bg-primary)] hover:bg-[var(--bg-secondary)] transition-colors"
                  >
                    <Icon icon="mdi:information-outline" width={18} height={18} />
                    Details
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex justify-center items-center min-h-[320px] rounded-2xl bg-[var(--bg-secondary)] border border-[var(--border-color)]">
          <NoData message="No certificates yet. Complete a course and generate your first certificate." />
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && selectedCertificate && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={() => setShowPreviewModal(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Certificate preview"
        >
          <div
            className="bg-[var(--bg-primary)] rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border-color)] shrink-0">
              <h2 className="text-lg font-semibold text-[var(--text-primary)] truncate pr-4">
                {selectedCertificate.certificate_name}
              </h2>
              <button
                type="button"
                onClick={() => setShowPreviewModal(false)}
                className="p-2 rounded-xl text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)] hover:text-[var(--text-primary)] transition-colors"
                aria-label="Close"
              >
                <Icon icon="mdi:close" width={24} height={24} />
              </button>
            </div>
            <div className="flex-1 overflow-auto flex items-center justify-center bg-[var(--bg-secondary)] min-h-[50vh] p-6">
              <img
                src={selectedCertificate.certificate_url}
                alt={selectedCertificate.certificate_name}
                className="max-w-full max-h-[75vh] w-auto h-auto object-contain rounded-xl shadow-lg"
              />
            </div>
            <div className="flex justify-end gap-3 px-5 py-4 border-t border-[var(--border-color)] shrink-0">
              <button
                type="button"
                onClick={() => setShowPreviewModal(false)}
                className="px-4 py-2.5 rounded-xl font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)] transition-colors"
              >
                Close
              </button>
              <button
                type="button"
                onClick={() => handleDownload(selectedCertificate.certificate_url)}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-white bg-[var(--primary-color)] hover:bg-[var(--primary-hover)] transition-colors"
              >
                <Icon icon="mdi:download" width={18} height={18} />
                Download
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedCertificate && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={() => setShowDetailsModal(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Certificate details"
        >
          <div
            className="bg-[var(--bg-primary)] rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border-color)] shrink-0">
              <h2 className="text-lg font-semibold text-[var(--text-primary)]">Certificate Information</h2>
              <button
                type="button"
                onClick={() => setShowDetailsModal(false)}
                className="p-2 rounded-xl text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)] hover:text-[var(--text-primary)] transition-colors"
                aria-label="Close"
              >
                <Icon icon="mdi:close" width={24} height={24} />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-6 space-y-4">
              <div>
                <label className="block text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wide mb-1">Course Name</label>
                <p className="px-4 py-3 rounded-xl bg-[var(--bg-secondary)] text-[var(--text-primary)] border border-[var(--border-color)]">
                  {selectedCertificate.course_name}
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wide mb-1">Certificate Title</label>
                <p className="px-4 py-3 rounded-xl bg-[var(--bg-secondary)] text-[var(--text-primary)] border border-[var(--border-color)]">
                  {selectedCertificate.certificate_name}
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wide mb-1">Trainee Name</label>
                <p className="px-4 py-3 rounded-xl bg-[var(--bg-secondary)] text-[var(--text-primary)] border border-[var(--border-color)]">
                  {selectedCertificate.user_name}
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wide mb-1">Trainee Email</label>
                <p className="px-4 py-3 rounded-xl bg-[var(--bg-secondary)] text-[var(--text-primary)] border border-[var(--border-color)]">
                  {selectedCertificate.user_email}
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wide mb-1">Created By</label>
                <p className="px-4 py-3 rounded-xl bg-[var(--bg-secondary)] text-[var(--text-primary)] border border-[var(--border-color)]">
                  {selectedCertificate.createdBy || '—'}
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wide mb-1">Issue Date</label>
                <p className="px-4 py-3 rounded-xl bg-[var(--bg-secondary)] text-[var(--text-primary)] border border-[var(--border-color)]">
                  {new Date(selectedCertificate.issued_date).toLocaleString()}
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 px-5 py-4 border-t border-[var(--border-color)] shrink-0">
              <button
                type="button"
                onClick={() => setShowDetailsModal(false)}
                className="px-4 py-2.5 rounded-xl font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)] transition-colors"
              >
                Close
              </button>
              <button
                type="button"
                onClick={() => handleDownload(selectedCertificate.certificate_url)}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-white bg-[var(--primary-color)] hover:bg-[var(--primary-hover)] transition-colors"
              >
                <Icon icon="mdi:download" width={18} height={18} />
                Download Certificate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserCertificates;
